/**
 * Admin V3 — Pricing — LegacyConfigPanel
 *
 * Strictly READ-ONLY display of the 7 legacy PricingConfig fields shipped by
 * SC-PR-2 (and preserved untouched by SC-OPS-1 per Owner Decision G3).
 *
 * SCOPE (Phase 1 binding — owner correction #1):
 *   - These fields are persisted but DECORATIVE under Schedule A. The
 *     pricing engine ignores them (Gate H boundary; engine consumes
 *     schedule_a only).
 *   - This panel is pure read-only display. There are NO editing controls.
 *     The parent (PricingConfigPage) passes these values through unchanged
 *     to its PUT body — they are sourced from the latest GET and never
 *     hardcoded.
 *
 * The 7 fields (per backend/app/models/schemas.py PricingConfig):
 *   1. base_fare              (€)
 *   2. price_per_km           (€/km)
 *   3. price_per_minute       (€/min)
 *   4. minimum_fare           (€)
 *   5. night_multiplier       (× factor — Gate F: engine ignores)
 *   6. weekend_multiplier     (× factor — Gate F: engine ignores)
 *   7. commission_rate        (fraction — Gate H decorative passthrough)
 *
 * Operators see this panel so they can verify the legacy state is what
 * they expect; they cannot accidentally drift it.
 */

import React from 'react';
import { History, Lock } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';

const labels = {
  title:        'Configuration héritée (décorative)',
  badge:        'Décorative sous Schedule A',
  hint:         'Ces champs sont persistés mais ignorés par le moteur de tarification (Gate H). Ils restent en lecture seule pour préserver la rétrocompatibilité ; la source de vérité tarifaire est Schedule A.',
  fields: {
    base_fare:          'Tarif de base',
    price_per_km:       'Prix par km',
    price_per_minute:   'Prix par minute',
    minimum_fare:       'Tarif minimum',
    night_multiplier:   'Multiplicateur nuit (Gate F : ignoré)',
    weekend_multiplier: 'Multiplicateur week-end (Gate F : ignoré)',
    commission_rate:    'Taux commission (Gate H : décoratif)',
  },
};

const FIELD_ORDER = [
  { key: 'base_fare',          suffix: '€'    },
  { key: 'price_per_km',       suffix: '€/km' },
  { key: 'price_per_minute',   suffix: '€/min'},
  { key: 'minimum_fare',       suffix: '€'    },
  { key: 'night_multiplier',   suffix: '×'    },
  { key: 'weekend_multiplier', suffix: '×'    },
  { key: 'commission_rate',    suffix: ''     }, // fraction
];

const formatValue = (v, suffix) => {
  if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) return '—';
  if (typeof v === 'number') {
    // Render commission_rate as percentage for readability; others to 2 decimals.
    if (suffix === '' && v <= 1) {
      return `${(v * 100).toFixed(1).replace('.', ',')} %`;
    }
    return `${v.toFixed(2).replace('.', ',')}${suffix ? ' ' + suffix : ''}`;
  }
  return String(v);
};

/**
 * Props:
 *   legacy — object containing the 7 legacy fields (sourced from GET /pricing/config)
 *
 * No onChange — strictly read-only.
 */
const LegacyConfigPanel = ({ legacy }) => {
  const safe = legacy || {};
  return (
    <SectionCard title={labels.title} icon={History} iconClass="bg-slate-100 text-slate-500">
      <div className="space-y-3" data-testid="adminv3-pricing-legacy-panel">
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] italic text-amber-800">
          <Lock className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="font-semibold">{labels.badge}.</span>
          <span>{labels.hint}</span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 text-left">Champ</th>
                <th className="px-4 py-2 text-left">Libellé</th>
                <th className="px-4 py-2 text-right">Valeur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {FIELD_ORDER.map(({ key, suffix }) => (
                <tr key={key} className="bg-white" data-testid={`adminv3-pricing-legacy-row-${key}`}>
                  <td className="px-4 py-2 font-mono text-[12px] text-slate-600">{key}</td>
                  <td className="px-4 py-2 text-slate-700">{labels.fields[key]}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      <Lock className="h-3 w-3 text-slate-400" />
                      <span data-testid={`adminv3-pricing-legacy-value-${key}`}>
                        {formatValue(safe[key], suffix)}
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
};

export default LegacyConfigPanel;
